import { useCallback, useEffect, useRef, useState } from "react";

export interface Page<T> {
  items: T[];
  hasMore: boolean;
}

export interface Infinite<T> {
  items: T[];
  /** Carregando a primeira página (troca de contexto) — mostrar skeleton. */
  initialLoading: boolean;
  /** Carregando uma página seguinte (append). */
  loadingMore: boolean;
  hasMore: boolean;
  /**
   * Callback ref para o sentinela no fim da lista. É callback (não objeto) de
   * propósito: o observer só é anexado quando o nó realmente monta — o que
   * acontece DEPOIS do skeleton inicial. Um ref-objeto anexaria cedo demais
   * (nó ainda null) e a paginação nunca dispararia.
   */
  sentinelRef: (node: HTMLDivElement | null) => void;
  reload: () => void;
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
}

/**
 * Lista com scroll infinito. `fetchPage(offset)` devolve uma página; quando o
 * sentinela entra na viewport e há mais, busca a próxima. Reseta quando `deps`
 * muda (ex.: busca ou troca de usuário).
 */
export function useInfinite<T>(
  fetchPage: (offset: number) => Promise<Page<T>>,
  deps: React.DependencyList,
): Infinite<T> {
  const [items, setItems] = useState<T[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const offsetRef = useRef(0);
  const busyRef = useRef(false);
  const hasMoreRef = useRef(false);
  const intersectingRef = useRef(false);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const observerRef = useRef<IntersectionObserver | null>(null);

  const load = useCallback(async (reset: boolean) => {
    if (busyRef.current) return;
    busyRef.current = true;
    if (reset) setInitialLoading(true);
    else setLoadingMore(true);
    const offset = reset ? 0 : offsetRef.current;
    try {
      const page = await fetchRef.current(offset);
      setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
      offsetRef.current = offset + page.items.length;
      hasMoreRef.current = page.hasMore;
      setHasMore(page.hasMore);
    } catch {
      if (reset) {
        setItems([]);
        hasMoreRef.current = false;
        setHasMore(false);
      }
    } finally {
      busyRef.current = false;
      setInitialLoading(false);
      setLoadingMore(false);
    }
    // Encadeia: se o sentinela continua visível e ainda há páginas, segue
    // carregando. Cobre o caso em que o sentinela já estava na viewport (e o
    // observer não dispara de novo por não haver mudança de interseção).
    if (intersectingRef.current && hasMoreRef.current && !busyRef.current) {
      void load(false);
    }
  }, []);

  // reset ao mudar as dependências (busca, usuário selecionado, versão)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    offsetRef.current = 0;
    void load(true);
  }, deps);

  // callback ref: (re)anexa o observer sempre que o sentinela monta/desmonta
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (node) {
        const io = new IntersectionObserver(
          (entries) => {
            const isIntersecting = entries[0]?.isIntersecting ?? false;
            intersectingRef.current = isIntersecting;
            if (isIntersecting && hasMoreRef.current && !busyRef.current) {
              void load(false);
            }
          },
          { rootMargin: "300px" },
        );
        io.observe(node);
        observerRef.current = io;
      } else {
        intersectingRef.current = false;
      }
    },
    [load],
  );

  const reload = useCallback(() => {
    offsetRef.current = 0;
    void load(true);
  }, [load]);

  return { items, initialLoading, loadingMore, hasMore, sentinelRef, reload, setItems };
}
